/**
 * Control Portal - Fleet & Asset Management
 * Vehicles, Drivers, and Assets across all tenants
 */
import { useState, useEffect, useCallback } from 'react';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Truck, Users, Package, Search, RefreshCw, Loader2, Plus,
  Wrench, CheckCircle, XCircle, Building2, Snowflake, Phone
} from 'lucide-react';

export default function FleetManagementPage() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vehicles');
  const [summary, setSummary] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = tenantFilter !== 'all' ? { tenant_id: tenantFilter } : {};
      
      const [summaryRes, tenantsRes] = await Promise.all([
        controlApi.getFleetSummary(params),
        controlApi.getTenants()
      ]);
      
      setSummary(summaryRes.data);
      setTenants(tenantsRes.data);
      
      if (activeTab === 'vehicles') {
        const res = await controlApi.getVehicles(params);
        setVehicles(res.data);
      } else if (activeTab === 'drivers') {
        const res = await controlApi.getDrivers(params);
        setDrivers(res.data);
      } else {
        const res = await controlApi.getAssets(params);
        setAssets(res.data);
      }
    } catch (error) {
      toast.error('Failed to load fleet data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, tenantFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      maintenance: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      inactive: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      on_leave: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      available: 'bg-green-500/20 text-green-400 border-green-500/30',
      in_use: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      retired: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return <Badge className={styles[status] || styles.inactive}>{status?.replace('_', ' ')}</Badge>;
  };

  const filteredVehicles = vehicles.filter(v => 
    v.vehicle_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.tenant_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDrivers = drivers.filter(d => 
    d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.tenant_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredAssets = assets.filter(a => 
    a.asset_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6 bg-[#05050a] min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-cyan-100 flex items-center gap-3" data-testid="fleet-title">
            <Truck className="w-7 h-7 text-cyan-400" />
            Fleet & Asset Management
          </h1>
          <p className="text-cyan-600 text-sm font-mono tracking-wide mt-1">MANAGE VEHICLES, DRIVERS, AND ASSETS</p>
        </div>
        <Button 
          onClick={fetchData} 
          className="bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          REFRESH
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="fleet-summary">
          <div className="bg-[#0a0a12] border border-cyan-500/20 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Truck className="w-5 h-5 text-cyan-400" />
              <span className="text-cyan-600 text-xs font-mono">VEHICLES</span>
            </div>
            <p className="text-2xl font-bold font-mono text-cyan-100">{summary.vehicles.total}</p>
            <div className="flex gap-2 mt-2 text-xs font-mono">
              <span className="text-green-400">{summary.vehicles.active} active</span>
              <span className="text-amber-400">{summary.vehicles.maintenance} maint</span>
            </div>
          </div>
          
          <div className="bg-[#0a0a12] border border-cyan-500/20 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Snowflake className="w-5 h-5 text-cyan-400" />
              <span className="text-cyan-600 text-xs font-mono">REFRIGERATED</span>
            </div>
            <p className="text-2xl font-bold font-mono text-cyan-400">{summary.vehicles.refrigerated}</p>
            <p className="text-xs text-cyan-700 mt-2 font-mono">{summary.vehicles.on_trip} on trips</p>
          </div>
          
          <div className="bg-[#0a0a12] border border-cyan-500/20 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-green-400" />
              <span className="text-cyan-600 text-xs font-mono">DRIVERS</span>
            </div>
            <p className="text-2xl font-bold font-mono text-cyan-100">{summary.drivers.total}</p>
            <div className="flex gap-2 mt-2 text-xs font-mono">
              <span className="text-green-400">{summary.drivers.active} active</span>
            </div>
          </div>
          
          <div className="bg-[#0a0a12] border border-cyan-500/20 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-5 h-5 text-purple-400" />
              <span className="text-cyan-600 text-xs font-mono">ASSETS</span>
            </div>
            <p className="text-2xl font-bold font-mono text-cyan-100">{summary.assets.total}</p>
            <div className="flex gap-2 mt-2 text-xs font-mono">
              <span className="text-green-400">{summary.assets.available} available</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-cyan-600 w-4 h-4" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#0a0a12] border-cyan-500/20 text-cyan-100 placeholder:text-cyan-700"
          />
        </div>
        <Select value={tenantFilter} onValueChange={setTenantFilter}>
          <SelectTrigger className="w-48 bg-[#0a0a12] border-cyan-500/20 text-cyan-100">
            <SelectValue placeholder="Customer" />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0a12] border-cyan-500/20">
            <SelectItem value="all" className="text-cyan-100">All Customers</SelectItem>
            {tenants.map(t => (
              <SelectItem key={t.id} value={t.id} className="text-cyan-100">{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#0a0a12] border border-cyan-500/20">
          <TabsTrigger value="vehicles" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 text-cyan-600">
            <Truck className="w-4 h-4 mr-2" />
            Vehicles ({vehicles.length})
          </TabsTrigger>
          <TabsTrigger value="drivers" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 text-cyan-600">
            <Users className="w-4 h-4 mr-2" />
            Drivers ({drivers.length})
          </TabsTrigger>
          <TabsTrigger value="assets" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 text-cyan-600">
            <Package className="w-4 h-4 mr-2" />
            Assets ({assets.length})
          </TabsTrigger>
        </TabsList>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles">
          <div className="bg-[#0a0a12] border border-cyan-500/20 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-cyan-500/20 bg-[#080810]">
                  <TableHead className="text-cyan-500 font-mono text-xs">VEHICLE</TableHead>
                  <TableHead className="text-cyan-500 font-mono text-xs">CUSTOMER</TableHead>
                  <TableHead className="text-cyan-500 font-mono text-xs">TYPE</TableHead>
                  <TableHead className="text-cyan-500 font-mono text-xs">REFRIGERATED</TableHead>
                  <TableHead className="text-cyan-500 font-mono text-xs">STATUS</TableHead>
                  <TableHead className="text-cyan-500 font-mono text-xs">CAPACITY</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : filteredVehicles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-cyan-600">
                      <Truck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      NO VEHICLES FOUND
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredVehicles.map((v) => (
                    <TableRow key={v.id} className="border-cyan-500/10">
                      <TableCell>
                        <p className="text-cyan-100 font-medium">{v.vehicle_number}</p>
                        <p className="text-cyan-600 text-xs font-mono">{v.make} {v.model} {v.year && `(${v.year})`}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-cyan-600" />
                          <span className="text-cyan-200">{v.tenant_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-cyan-200 capitalize">{v.vehicle_type}</TableCell>
                      <TableCell>
                        {v.is_refrigerated ? (
                          <Snowflake className="w-5 h-5 text-cyan-400" />
                        ) : (
                          <span className="text-cyan-700">-</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(v.status)}</TableCell>
                      <TableCell className="text-cyan-200 font-mono">
                        {v.capacity_kg ? `${v.capacity_kg} kg` : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Drivers Tab */}
        <TabsContent value="drivers">
          <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Driver</TableHead>
                  <TableHead className="text-slate-400">Customer</TableHead>
                  <TableHead className="text-slate-400">Contact</TableHead>
                  <TableHead className="text-slate-400">License</TableHead>
                  <TableHead className="text-slate-400">Assigned Vehicle</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : filteredDrivers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No drivers found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDrivers.map((d) => (
                    <TableRow key={d.id} className="border-slate-800">
                      <TableCell className="text-white font-medium">{d.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-300">{d.tenant_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {d.phone && (
                          <div className="flex items-center gap-2 text-slate-300">
                            <Phone className="w-4 h-4 text-slate-500" />
                            {d.phone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="text-slate-300">{d.license_number || '-'}</p>
                        {d.license_expiry && (
                          <p className="text-slate-500 text-xs">Exp: {d.license_expiry}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {d.assigned_vehicle_number || '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(d.status)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Assets Tab */}
        <TabsContent value="assets">
          <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Asset Code</TableHead>
                  <TableHead className="text-slate-400">Customer</TableHead>
                  <TableHead className="text-slate-400">Type</TableHead>
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Calibration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : filteredAssets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                      <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No assets found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAssets.map((a) => (
                    <TableRow key={a.id} className="border-slate-800">
                      <TableCell className="text-white font-mono">{a.asset_code}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-300">{a.tenant_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300 capitalize">
                        {a.asset_type?.replace('_', ' ')}
                      </TableCell>
                      <TableCell className="text-slate-300">{a.name || '-'}</TableCell>
                      <TableCell>{getStatusBadge(a.status)}</TableCell>
                      <TableCell className="text-slate-300">
                        {a.next_calibration_at ? (
                          <span className="text-xs">Next: {new Date(a.next_calibration_at).toLocaleDateString()}</span>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
